import { useCallback, useEffect, useRef, useState } from 'react'

export const useAutoScroll = (dependency: unknown) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const isUserScrolledUp = useRef(false)
  const [isAwayFromBottom, setIsAwayFromBottom] = useState(false)

  // Follow the bottom again without moving the scroll position now. The next
  // content change scrolls, so a caller adding content in the same commit lands
  // at the real bottom instead of the height the list had before it grew.
  const stickToBottom = useCallback(() => {
    isUserScrolledUp.current = false
    setIsAwayFromBottom(false)
  }, [])

  const scrollToBottom = useCallback(() => {
    const container = containerRef.current
    if (!container) return
    container.scrollTop = container.scrollHeight
    stickToBottom()
  }, [stickToBottom])

  useEffect(() => {
    if (!isUserScrolledUp.current) {
      scrollToBottom()
    }
  }, [dependency, scrollToBottom])

  const handleScroll = useCallback(() => {
    const container = containerRef.current
    if (!container) return

    const threshold = 40
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight
    isUserScrolledUp.current = distanceFromBottom > threshold
    setIsAwayFromBottom(isUserScrolledUp.current)
  }, [])

  return {
    containerRef,
    handleScroll,
    scrollToBottom,
    stickToBottom,
    isAwayFromBottom,
  }
}
